// src/stores/accessStore.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import svc from "@/services/accessService";
import { getActiveCompanyId } from "@/services/_headers.util";
import { useAuthStore } from "@/stores/authStore";

export const useAccessStore = defineStore("access", () => {
  const loading = ref(false);
  const loaded = ref(false);

  const status = ref("none");
  const paymentStatus = ref(null);
  const companyStatus = ref(null);
  const planName = ref(null);
  const planId = ref(null);
  const billingPeriod = ref(null);
  const modules = ref([]);
  const disabledModules = ref([]);
  const fullAccess = ref(false);
  const trialEndsAt = ref(null);
  const endsAt = ref(null);
  const licenseStartedAt = ref(null);
  const licenseExpiresAt = ref(null);
  const gracePeriodEndsAt = ref(null);
  const gracePeriodMonths = ref(null);
  const renewalAmount = ref(null);
  const subscriptionId = ref(null);

  // ✅ NEW: remember which company we loaded access for
  const lastCompanyId = ref(null);

  // shared in-flight promise
  let _fetchPromise = null;

  function setEmptyAccess() {
    status.value = "none";
    paymentStatus.value = null;
    companyStatus.value = null;
    planName.value = null;
    planId.value = null;
    billingPeriod.value = null;
    modules.value = [];
    disabledModules.value = [];
    fullAccess.value = false;
    trialEndsAt.value = null;
    endsAt.value = null;
    licenseStartedAt.value = null;
    licenseExpiresAt.value = null;
    gracePeriodEndsAt.value = null;
    gracePeriodMonths.value = null;
    renewalAmount.value = null;
    subscriptionId.value = null;

    loaded.value = true;
  }

  function resetAccess() {
    loaded.value = false;
    loading.value = false;
    _fetchPromise = null;

    status.value = "none";
    paymentStatus.value = null;
    companyStatus.value = null;
    planName.value = null;
    planId.value = null;
    billingPeriod.value = null;
    modules.value = [];
    disabledModules.value = [];
    fullAccess.value = false;
    trialEndsAt.value = null;
    endsAt.value = null;
    licenseStartedAt.value = null;
    licenseExpiresAt.value = null;
    gracePeriodEndsAt.value = null;
    gracePeriodMonths.value = null;
    renewalAmount.value = null;
    subscriptionId.value = null;

    lastCompanyId.value = null;
  }

  async function fetchAccess({ force = false } = {}) {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) {
      status.value = "none";
      modules.value = [];
      disabledModules.value = [];
      loaded.value = false;
      lastCompanyId.value = null;
      return;
    }

    const activeCompanyId = getActiveCompanyId() || null;

    // ✅ if company changed since last load, force reload
    if (loaded.value && lastCompanyId.value !== activeCompanyId) {
      loaded.value = false;
      _fetchPromise = null;
      force = true;
    }

    // SuperAdmin + no company → empty access
    if (
      auth.user &&
      auth.user.roles?.includes("SuperAdmin") &&
      !activeCompanyId
    ) {
      lastCompanyId.value = null;
      setEmptyAccess();
      return;
    }

    // already loaded and not forcing
    if (loaded.value && !force) return;

    // in flight
    if (_fetchPromise) return _fetchPromise;

    loading.value = true;

    _fetchPromise = (async () => {
      try {
        const res = await svc.getAccess();
        const data = res.data || {};

        status.value = data.status ?? "none";
        companyStatus.value = data.company_status ?? null;
        paymentStatus.value = data.payment_status ?? null;
        planName.value = data.plan_name ?? null;
        planId.value = data.plan_id ?? null;
        billingPeriod.value = data.billing_period ?? null;
        modules.value = Array.isArray(data.modules) ? data.modules : [];
        disabledModules.value = Array.isArray(data.disabled_modules) ? data.disabled_modules : [];
        fullAccess.value = !!data.full_access;
        trialEndsAt.value = data.trial_ends_at ?? null;
        endsAt.value = data.ends_at ?? null;
        licenseStartedAt.value = data.license_started_at ?? null;
        licenseExpiresAt.value = data.license_expires_at ?? null;
        gracePeriodEndsAt.value = data.grace_period_ends_at ?? null;
        gracePeriodMonths.value = data.grace_period_months ?? null;
        renewalAmount.value = data.renewal_amount ?? null;
        subscriptionId.value = data.subscription_id ?? null;

        // ✅ lock-in which company these modules belong to
        lastCompanyId.value = activeCompanyId;

        loaded.value = true;
        return res;
      } catch (e) {
        console.error(e);
        // Keep the last known good access snapshot on transient failures
        // (common after sleep/wake when the network is not ready yet).
        if (!loaded.value) {
          status.value = "none";
          paymentStatus.value = null;
          companyStatus.value = null;
          planName.value = null;
          planId.value = null;
          billingPeriod.value = null;
          modules.value = [];
          disabledModules.value = [];
          fullAccess.value = false;
          trialEndsAt.value = null;
          endsAt.value = null;
          licenseStartedAt.value = null;
          licenseExpiresAt.value = null;
          gracePeriodEndsAt.value = null;
          gracePeriodMonths.value = null;
          renewalAmount.value = null;
          subscriptionId.value = null;
          lastCompanyId.value = activeCompanyId;
          loaded.value = true;
        }
      } finally {
        loading.value = false;
        _fetchPromise = null;
      }
    })();

    return _fetchPromise;
  }

  // ✅ allow force from router or plan selection
  async function ensureAccess(force = false) {
    await fetchAccess({ force });
  }

  function hasModule(key) {
    const auth = useAuthStore();

    // SuperAdmin bypass
    if (auth.user && auth.user.roles?.includes("SuperAdmin")) return true;
    if (fullAccess.value) return true;

    if (!key) return true;
    return modules.value.includes(key);
  }

  function isModuleDisabledByCompany(key) {
    if (!key) return false;
    return disabledModules.value.includes(key);
  }

  // ---------- Banner helpers ----------
  const trialEndingSoon = computed(() => {
    if (status.value !== "trialing" || !trialEndsAt.value) return false;
    const diffDays =
      (new Date(trialEndsAt.value).getTime() - Date.now()) /
      (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 3;
  });

  const gracePeriodActive = computed(() => status.value === "grace_period");

  const graceRemainingText = computed(() => {
    if (!gracePeriodActive.value || !gracePeriodEndsAt.value) return "";
    const diffMs = new Date(gracePeriodEndsAt.value).getTime() - Date.now();
    const totalDays = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    const months = Math.floor(totalDays / 30);
    const days = totalDays % 30;

    if (months > 0 && days > 0) {
      return `${months} ${months === 1 ? "month" : "months"} ${days} ${days === 1 ? "day" : "days"}`;
    }
    if (months > 0) {
      return `${months} ${months === 1 ? "month" : "months"}`;
    }
    return `${totalDays} ${totalDays === 1 ? "day" : "days"}`;
  });

  function getBannerDismissKey() {
    if (!subscriptionId.value) return null;
    return `subBannerDismissed:${subscriptionId.value}:${status.value}:${gracePeriodEndsAt.value || trialEndsAt.value || ""}`;
  }

  function isBannerDismissed() {
    const key = getBannerDismissKey();
    if (!key) return false;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      localStorage.removeItem(key);
      return false;
    }
    return true;
  }

  function dismissBanner() {
    const key = getBannerDismissKey();
    if (!key) return;
    const sixHoursFromNow = Date.now() + 6 * 60 * 60 * 1000;
    localStorage.setItem(key, String(sixHoursFromNow));
  }

  const shouldShowBanner = computed(() => {
    if (isBannerDismissed()) return false;
    return (
      trialEndingSoon.value ||
      gracePeriodActive.value
    );
  });

  return {
    loading,
    loaded,
    status,
    paymentStatus,
    companyStatus,
    planName,
    planId,
    billingPeriod,
    modules,
    disabledModules,
    fullAccess,
    trialEndsAt,
    endsAt,
    licenseStartedAt,
    licenseExpiresAt,
    gracePeriodEndsAt,
    gracePeriodMonths,
    renewalAmount,
    subscriptionId,
    lastCompanyId,

    fetchAccess,
    ensureAccess,
    resetAccess,
    hasModule,
    isModuleDisabledByCompany,

    trialEndingSoon,
    gracePeriodActive,
    graceRemainingText,
    shouldShowBanner,
    dismissBanner,
  };
});
